import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import { fetchCalidadObjetivos } from "../api/objetivos";
import type { CalidadObjetivo, CalidadObjetivoFilters, CalidadObjetivoMeta } from "../types/objetivos";

const emptyMeta: CalidadObjetivoMeta = { total: 0, years: [] };

export function useCalidadObjetivos(filters: CalidadObjetivoFilters, enabled = true) {
  const [objetivos, setObjetivos] = useState<CalidadObjetivo[]>([]);
  const [meta, setMeta] = useState<CalidadObjetivoMeta>(emptyMeta);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso900114001LatestRequest();
  const requestFilters = useMemo<CalidadObjetivoFilters>(() => ({ ...filters, search: debouncedSearch }), [debouncedSearch, filters.anio, filters.cerrado]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const load = useCallback(async (force = true) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => fetchCalidadObjetivos(requestFilters, signal), force);
      if (!response) return;
      setObjetivos(response.data);
      setMeta(response.meta);
    } catch {
      setError("No se pudieron cargar los objetivos de calidad.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [enabled, hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void load(false); }, [load]);

  return { objetivos, meta, loading, error, reload: load };
}
